import { useMemo } from 'react'

import { useParams } from 'react-router'

import { Page } from '@/components/layout/page/page.tsx'
import { CopyTextButton } from '@/components/ui/copy-text-button'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { TrackingLinksInspectView } from '@/modules/tracking/links/components/inspect/tracking-links-insepct-view'
import { TrackingLinksInspectViewSkeleton } from '@/modules/tracking/links/components/inspect/tracking-links-inspect-view-skeleton.tsx'
import { useLinkTrackingById } from '@/modules/tracking/links/query/link-tracking-id-query'
import type { LinkType } from '@/modules/tracking/links/types'
import { AppRoutes } from '@/utils/constants'
import { formatDate } from '@/utils/time'

interface TrackingLinksInspectPageProps {
  type: LinkType
}

export const TrackingLinksInspectPage = ({
  type,
}: TrackingLinksInspectPageProps) => {
  const { linkId } = useParams()
  const { data, isFetching, updatePromotionCost } = useLinkTrackingById({
    type,
    id: parseInt(linkId || '-1'),
  })
  const { userSettings } = useAccount()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const { createdAt, typeIsTracking } = useMemo(
    () => ({
      createdAt: formatDate(new Date(data?.link?.createdAt || 0)),
      typeIsTracking: type === 'Tracking',
    }),
    [data?.link?.createdAt, type],
  )

  return (
    <Page
      className="mb-2"
      title={data?.link?.name || 'Loading...'}
      titleClassName="text-3xl mt-6"
      backToPage={{
        name: typeIsTracking ? 'Tracking Links' : 'Free Trial Links',
        url: typeIsTracking
          ? AppRoutes.Creator.ChatterTracking.Links.List.toString()
          : AppRoutes.Creator.ChatterTracking.FreeTrialLinks.List.toString(),
      }}
      titleOptions={
        <CopyTextButton
          text={data?.link?.url || ''}
          toastMessage="Link copied to clipboard"
          className="ml-4 h-6 w-6"
        />
      }
    >
      <div className="mb-6 text-secondary-300">Created: {createdAt}</div>
      {isFetching ? (
        <TrackingLinksInspectViewSkeleton />
      ) : (
        <TrackingLinksInspectView
          data={data}
          isFetching={isFetching}
          type={type}
          updatePromotionCost={updatePromotionCost}
          showCents={shouldShowCents}
        />
      )}
    </Page>
  )
}
